import { User, Users, Building2 } from "lucide-react";
import React, { useState } from "react";
import { Pagination, Spin } from "antd";

const TeamTable = ({ teamData }) => {
  const [activeTab, setActiveTab] = useState("team");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentManagementPage, setCurrentManagementPage] = useState(1);
  const itemsPerPage = 3;

  if (!teamData) {
    return (
      <div className="flex flex-col h-full">
        {/* Tab Headers */}
        <div className="flex border-b border-gray-200 mb-2">
          <button
            className="flex-1 py-2 px-3 text-sm font-medium text-gray-500"
            disabled
          >
            <Users className="h-4 w-4 inline mr-1" />
            My Team
          </button>
          <button
            className="flex-1 py-2 px-3 text-sm font-medium text-gray-500"
            disabled
          >
            <Building2 className="h-4 w-4 inline mr-1" />
            Management
          </button>
        </div>
        
        {/* Loading State */}
        <div className="flex-1 flex items-center justify-center">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  const { teamMembers, colleagues, managementChain } = teamData;
  
  // Determine which data to show for team tab
  const teamDisplayData = teamMembers && teamMembers.length > 0 ? teamMembers : colleagues;
  const teamTabTitle = teamMembers && teamMembers.length > 0 ? "My Team" : "Our Team";

  // Sort management chain by level (descending - high to low)
  const sortedManagementChain = managementChain ? [...managementChain].sort((a, b) => b.level - a.level) : [];

  // Pagination logic for team tab
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = teamDisplayData?.slice(startIndex, endIndex) || [];

  // Pagination logic for management tab
  const managementStartIndex = (currentManagementPage - 1) * itemsPerPage;
  const managementEndIndex = managementStartIndex + itemsPerPage;
  const currentManagementPageData = sortedManagementChain?.slice(managementStartIndex, managementEndIndex) || [];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleManagementPageChange = (page) => {
    setCurrentManagementPage(page);
  };

  return (
    <div className="flex flex-col h-full cursor-pointer">
      {/* Tab Headers */}
      <div className="flex border-b border-gray-200 mb-2">
        <button
          onClick={() => setActiveTab("team")}
          className={`flex-1 py-2 px-3 text-sm font-medium transition-colors cursor-pointer ${
            activeTab === "team"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Users className="h-4 w-4 inline mr-1" />
          {teamTabTitle}
        </button>
        <button
          onClick={() => setActiveTab("management")}
          className={`flex-1 py-2 px-3 text-sm font-medium transition-colors cursor-pointer ${
            activeTab === "management"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Building2 className="h-4 w-4 inline mr-1" />
          Management
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "team" && (
          <div className="space-y-1">
            {currentPageData.length > 0 ? (
              currentPageData.map((member) => (
                <div
                  key={member._id}
                  className="flex items-center justify-between border-b border-gray-100 py-1 px-3 hover:bg-gray-50"
                >
                  <User className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="flex-1 ml-2 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {member.name}
                    </h4>
                    <p className="text-xs text-gray-500 truncate">
                      {member.designation}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500 text-sm">
                No team members found
              </div>
            )}
            
            {/* Antd Pagination */}
            {teamDisplayData && teamDisplayData.length > itemsPerPage && (
              <div className="flex justify-center py-3 border-t border-gray-200 mt-2">
                <Pagination
                  current={currentPage}
                  total={teamDisplayData.length}
                  pageSize={itemsPerPage}
                  onChange={handlePageChange}
                  size="small"
                  showSizeChanger={false}
                  showQuickJumper={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === "management" && (
          <div className="space-y-1">
            {currentManagementPageData.length > 0 ? (
              currentManagementPageData.map((manager) => (
                <div
                  key={manager._id}
                  className="flex items-center justify-between border-b border-gray-100 py-1 px-3 hover:bg-gray-50"
                >
                  <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="flex-1 ml-2 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {manager.name}
                    </h4>
                    <p className="text-xs text-gray-500 truncate">
                      {manager.designation}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500 text-sm">
                No management information found
              </div>
            )}
            
            {/* Antd Pagination for Management */}
            {sortedManagementChain && sortedManagementChain.length > itemsPerPage && (
              <div className="flex justify-center py-3 border-t border-gray-200 mt-2">
                <Pagination
                  current={currentManagementPage}
                  total={sortedManagementChain.length}
                  pageSize={itemsPerPage}
                  onChange={handleManagementPageChange}
                  size="small"
                  showSizeChanger={false}
                  showQuickJumper={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamTable;
